import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "../services/api";
import { Card, PageHeader, Select, Skeleton } from "../components/ui";
import { AnimatedNumber } from "../components/feedback";
import { can } from "../types";
import { useUi } from "../stores/ui";

const COLORS = ["#EC4899", "#38BDF8", "#34D399", "#FBBF24", "#C084FC"];

export function AnalyticsPage() {
  const user = useUi((s) => s.user);
  const [range, setRange] = useState("30d");
  const overview = useQuery({
    queryKey: ["overview", range],
    queryFn: () => api<Record<string, number | string>>(`/api/analytics/overview?range=${range}`),
    enabled: can(user, "VIEW_ANALYTICS"),
  });
  const activity = useQuery({
    queryKey: ["activity-charts", range],
    queryFn: () =>
      api<{
        series: { points: { label: string; interviews: number; trainings: number }[] };
        breakdown: { interviews: number; trainings: number };
        monthly: { label: string; interviews: number; trainings: number; total: number }[];
      }>(`/api/analytics/activity?range=${range}`),
    enabled: can(user, "VIEW_ANALYTICS"),
  });
  const teams = useQuery({
    queryKey: ["team-charts", range],
    queryFn: () => api<{ teams: { name: string; color: string; interviews: number; trainings: number }[] }>(`/api/analytics/teams?range=${range}`),
    enabled: can(user, "VIEW_ANALYTICS"),
  });
  const members = useQuery({
    queryKey: ["member-charts", range],
    queryFn: () =>
      api<{
        top: { displayName: string; team: string; interviews: number; trainings: number; total: number }[];
        completion: { EXCEEDED: number; MET: number; CLOSE: number; BELOW: number };
      }>(`/api/analytics/members?range=${range}`),
    enabled: can(user, "VIEW_ANALYTICS"),
  });

  if (!can(user, "VIEW_ANALYTICS")) {
    return <p className="text-zinc-400">You do not have permission to access this section.</p>;
  }
  if (overview.isLoading) return <Skeleton className="h-96" />;
  const o = overview.data ?? {};
  const pie = [
    { name: "Interviews", value: Number(activity.data?.breakdown.interviews ?? 0) },
    { name: "Trainings", value: Number(activity.data?.breakdown.trainings ?? 0) },
  ];
  const completion = members.data?.completion;
  const completionPie = completion
    ? [
        { name: "Exceeded", value: completion.EXCEEDED },
        { name: "Met", value: completion.MET },
        { name: "Close", value: completion.CLOSE },
        { name: "Below", value: completion.BELOW },
      ]
    : [];
  const activePie = [
    { name: "Active", value: Number(o.activeMembers ?? 0) },
    { name: "Inactive", value: Number(o.inactiveMembers ?? 0) },
  ];

  return (
    <div>
      <PageHeader
        title="Analytics"
        subtitle="Server-side activity reporting. These numbers inform decisions — they are not automatic discipline."
        actions={
          <Select value={range} onChange={(e) => setRange(e.target.value)} className="w-full sm:w-40">
            <option value="7d">7 Days</option>
            <option value="30d">30 Days</option>
            <option value="90d">90 Days</option>
            <option value="year">Year</option>
            <option value="all">All Time</option>
          </Select>
        }
      />
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {[
          ["Total HR Members", o.totalHrMembers],
          ["Active Members", o.activeMembers],
          ["Inactive Members", o.inactiveMembers],
          ["Interviews This Month", o.interviewsThisMonth],
          ["Trainings This Month", o.trainingsThisMonth],
          ["Total Activities This Month", o.totalActivitiesThisMonth],
          ["Requirement Completion %", o.completionPercent],
          ["Avg Activities / Member", o.averageActivitiesPerMember],
        ].map(([label, value]) => (
          <Card key={String(label)}>
            <p className="text-sm text-zinc-400">{label}</p>
            <p className="mt-2 text-2xl font-semibold sm:text-3xl"><AnimatedNumber value={Number(value ?? 0)} /></p>
          </Card>
        ))}
      </div>
      <p className="mt-3 text-sm text-zinc-500">Most active team: {String(o.mostActiveTeam ?? "—")}</p>
      <div className="mt-6 grid gap-4 xl:grid-cols-2">
        <Card className="min-w-0 overflow-hidden">
          <h3 className="mb-3 font-semibold">Activity over time</h3>
          <div className="h-56 w-full min-w-0 sm:h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={activity.data?.series.points ?? []}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="label" stroke="#a1a1aa" />
                <YAxis stroke="#a1a1aa" />
                <Tooltip />
                <Legend />
                <Line dataKey="interviews" stroke="#EC4899" />
                <Line dataKey="trainings" stroke="#38BDF8" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card>
          <h3 className="mb-3 font-semibold">Activity breakdown</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={pie} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80}>
                {pie.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>
        <Card>
          <h3 className="mb-3 font-semibold">Team performance</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={(teams.data?.teams ?? []).map((t) => ({ name: t.name, total: t.interviews + t.trainings }))}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="name" stroke="#a1a1aa" />
              <YAxis stroke="#a1a1aa" />
              <Tooltip />
              <Bar dataKey="total" fill="#EC4899" radius={8} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card>
          <h3 className="mb-3 font-semibold">Active vs Inactive</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={activePie} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80}>
                <Cell fill="#34D399" />
                <Cell fill="#FB7185" />
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>
        <Card>
          <h3 className="mb-3 font-semibold">Monthly trend</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={activity.data?.monthly ?? []}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="label" stroke="#a1a1aa" />
              <YAxis stroke="#a1a1aa" />
              <Tooltip />
              <Line dataKey="interviews" stroke="#EC4899" />
              <Line dataKey="trainings" stroke="#38BDF8" />
              <Line dataKey="total" stroke="#FBBF24" />
            </LineChart>
          </ResponsiveContainer>
        </Card>
        <Card>
          <h3 className="mb-3 font-semibold">Requirement completion</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={completionPie} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80}>
                {completionPie.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>
      <Card className="mt-4 overflow-x-auto">
        <h3 className="mb-3 font-semibold">Top contributors</h3>
        <table className="w-full min-w-[32rem] text-left text-sm">
          <thead className="text-zinc-400"><tr><th>Member</th><th>Team</th><th>Interviews</th><th>Trainings</th><th>Total</th></tr></thead>
          <tbody>
            {(members.data?.top ?? []).map((row) => (
              <tr key={row.displayName} className="border-t border-white/5">
                <td className="py-2">{row.displayName}</td>
                <td>{row.team}</td>
                <td>{row.interviews}</td>
                <td>{row.trainings}</td>
                <td>{row.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
